import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { AppSkeleton } from 'src/components/common/atoms/AppSkeleton';
import { PhoneNumberLink } from 'src/components/shien/atoms/PhoneNumberLink';
import {
  appConst,
  cohabitationSeparationCodes,
  gradeCodes,
  mentalDisabilityHealthAndWelfareHandbookCodes,
  nursingCareLevelCodes,
  presenceAbsenceCodes,
  progressStatusCodes,
  rehabilitationNotebookCodes,
  sexCodes,
  solitudeCohabitationCodes,
} from 'src/constants/common';
import { AppListItem, AppListSectionData } from 'src/types/common/AppListSectionData';
import { AdditionalItemSetting, Prs2 } from 'src/types/common/Prs2';
import { calculateCurrentAge } from 'src/utilities/dateUtil';

const buildSuspendDataList = (
  checkVisible: (prsItemKey: string) => boolean,
  additionalItemSettings?: AdditionalItemSetting[],
): AppListSectionData[] => {
  const basicItems = [
    { title: '氏名', value: <AppSkeleton /> },
    { title: '氏名カナ', value: <AppSkeleton /> },
    checkVisible('sex') && { title: '性別', value: <AppSkeleton /> },
    checkVisible('birthday') && { title: '生年月日', value: <AppSkeleton /> },
    checkVisible('age') && { title: '年齢', value: <AppSkeleton /> },
    checkVisible('zip') && { title: '郵便番号', value: <AppSkeleton /> },
    checkVisible('address') && { title: '住所', value: <AppSkeleton /> },
    checkVisible('subaddress') && { title: '登録住所以外の居住地', value: <AppSkeleton /> },
    checkVisible('telno') && { title: '連絡先', value: <AppSkeleton /> },
    checkVisible('telno2') && { title: '連絡先２', value: <AppSkeleton /> },
    checkVisible('fax') && { title: 'FAX', value: <AppSkeleton /> },
    { title: '本人確認', value: <AppSkeleton /> },
    checkVisible('resident_agreement') && { title: '住基系外部提供同意', value: <AppSkeleton /> },
    checkVisible('municipality_agreement') && {
      title: '個別避難計画外部提供同意(自治体職員のみ)',
      value: <AppSkeleton />,
    },
    checkVisible('status') && { title: '進捗状況', value: <AppSkeleton /> },
  ].filter(Boolean) as AppListItem[];

  const considerationItems = [
    checkVisible('prskubun') && { title: '区分', value: <AppSkeleton /> },
    checkVisible('family_structure') && { title: (
            <>
              世帯状況
              <br />
              /家族構成
            </>
          ), value: <AppSkeleton /> },
    checkVisible('solitude_cohabitation') && { title: '独居/同居', value: <AppSkeleton /> },
    { title: '所属地域', value: <AppSkeleton /> },
    checkVisible('primary_doctor') && { title: '医療機関', value: <AppSkeleton /> },
    checkVisible('physical_disability_certificate') && { title: '身体障害手帳', value: <AppSkeleton /> },
    checkVisible('rehabilitation_notebook') && { title: '療育手帳', value: <AppSkeleton /> },
    checkVisible('mental_disability_health_and_welfare_handbook') && {
      title: '精神障害者保健福祉手帳',
      value: <AppSkeleton />,
    },
    checkVisible('nursing_care_level') && { title: '要介護度', value: <AppSkeleton /> },
    checkVisible('intractable_disease') && { title: '難病指定の有無', value: <AppSkeleton /> },
    checkVisible('travel_medicine') && { title: '携行薬品等', value: <AppSkeleton /> },
  ].filter(Boolean) as AppListItem[];

  const emergencyContactItems = [
    checkVisible('emergency_contact_name') && { title: '氏名', value: <AppSkeleton /> },
    checkVisible('emergency_contact_name_kana') && { title: '氏名カナ', value: <AppSkeleton /> },
    checkVisible('emergency_contact_relationship') && { title: '続柄', value: <AppSkeleton /> },
    checkVisible('emergency_contact_address') && { title: '住所', value: <AppSkeleton /> },
    checkVisible('emergency_contact_phone_number') && { title: '電話番号', value: <AppSkeleton /> },
    checkVisible('emergency_contact_phone_number2') && { title: '電話番号２', value: <AppSkeleton /> },
    checkVisible('emergency_contact_solitude_cohabitation') && { title: '同居/別居', value: <AppSkeleton /> },
  ].filter(Boolean) as AppListItem[];

  const emergencyContact2Items = [
    checkVisible('emergency_contact2_name') && { title: '氏名', value: <AppSkeleton /> },
    checkVisible('emergency_contact2_name_kana') && { title: '氏名カナ', value: <AppSkeleton /> },
    checkVisible('emergency_contact2_relationship') && { title: '続柄', value: <AppSkeleton /> },
    checkVisible('emergency_contact2_address') && { title: '住所', value: <AppSkeleton /> },
    checkVisible('emergency_contact2_phone_number') && { title: '電話番号', value: <AppSkeleton /> },
    checkVisible('emergency_contact2_phone_number2') && { title: '電話番号２', value: <AppSkeleton /> },
    checkVisible('emergency_contact2_solitude_cohabitation') && { title: '同居/別居', value: <AppSkeleton /> },
  ].filter(Boolean) as AppListItem[];

  const emergencyContact3Items = [
    checkVisible('emergency_contact3_name') && { title: '氏名', value: <AppSkeleton /> },
    checkVisible('emergency_contact3_name_kana') && { title: '氏名カナ', value: <AppSkeleton /> },
    checkVisible('emergency_contact3_relationship') && { title: '続柄', value: <AppSkeleton /> },
    checkVisible('emergency_contact3_address') && { title: '住所', value: <AppSkeleton /> },
    checkVisible('emergency_contact3_phone_number') && { title: '電話番号', value: <AppSkeleton /> },
    checkVisible('emergency_contact3_phone_number2') && { title: '電話番号２', value: <AppSkeleton /> },
    checkVisible('emergency_contact3_solitude_cohabitation') && { title: '同居/別居', value: <AppSkeleton /> },
  ].filter(Boolean) as AppListItem[];

  const remarksItems = [checkVisible('supporter_remarks') && { value: <AppSkeleton /> }].filter(Boolean) as AppListItem[];

  const additionalItems: AppListItem[] =
    additionalItemSettings?.map((setting) => ({
      title: setting.additionalItemName,
      value: <AppSkeleton />,
    })) ?? [];

  return [
    { header: '基本情報', items: basicItems },
    ...(considerationItems.length > 0 ? [{ header: '配慮情報', items: considerationItems }] : []),
    ...(emergencyContactItems.length > 0 ? [{ header: '緊急連絡先', items: emergencyContactItems }] : []),
    ...(emergencyContact2Items.length > 0 ? [{ header: '緊急連絡先２', items: emergencyContact2Items }] : []),
    ...(emergencyContact3Items.length > 0 ? [{ header: '緊急連絡先３', items: emergencyContact3Items }] : []),
    ...(remarksItems.length > 0 ? [{ header: '備考', items: remarksItems }] : []),
    ...(additionalItems.length > 0 ? [{ header: '追加情報', items: additionalItems }] : []),
  ];
};

export const usePrsSectionDataList = (
  clickPhoneNumberCallback: (phoneNumber: string, message: ReactNode) => void,
  prs?: Prs2,
  additionalItemSettings?: AdditionalItemSetting[],
  isVisible?: (prsItemKey: string) => boolean,
) => {
  const checkVisible = useCallback(
    (prsItemKey: string): boolean => isVisible?.(prsItemKey) ?? false,
    [isVisible]
  );

  const suspendDataList = useMemo(
    () => buildSuspendDataList(checkVisible, additionalItemSettings),
    [checkVisible, additionalItemSettings]
  );

  const [sectionDataList, setSectionDataList] = useState<AppListSectionData[]>(suspendDataList);

  useEffect(() => {
    if (prs) {
      const age = calculateCurrentAge(prs.birthday);

      const basicItems = [
        { title: '氏名', value: `${prs.fName} ${prs.sName}` },
        { title: '氏名カナ', value: `${prs.kfName} ${prs.ksName}` },
        checkVisible('sex') && { title: '性別', value: sexCodes.find((x) => x.code === prs.sex)?.Label },
        checkVisible('birthday') && {
          title: '生年月日',
          value: prs.birthday?.length === 8
            ? `${prs.birthday.slice(0, 4)}/${prs.birthday.slice(4, 6)}/${prs.birthday.slice(6, 8)}`
            : prs.birthday ?? '',
        },        
        checkVisible('age') && { title: '年齢', value: age ? `${age}歳` : '' },
        checkVisible('zip') && {title: '郵便番号', value: prs.zip},
        checkVisible('address') && {title: '住所', value: prs.address},
        checkVisible('subaddress') && {title: '登録住所以外の居住地', value: prs.subAddress},
        checkVisible('telno') &&{
          title: '連絡先',
          value: prs.telNo && (
            <PhoneNumberLink 
              phoneNumber={prs.telNo}
              message="対象者に電話をかけ、安否や避難先などを確認しましょう。"
              onClick={clickPhoneNumberCallback}            
            />
          ),
        },
        checkVisible('telno2') &&{
          title: '連絡先２',
          value: prs.telNo2 && (
            <PhoneNumberLink 
              phoneNumber={prs.telNo2}
              message="対象者に電話をかけ、安否や避難先などを確認しましょう。"
              onClick={clickPhoneNumberCallback}            
            />
          ),
        },
        checkVisible('fax') && {title: 'FAX', value: prs.fax},
        { title: '本人確認', value: prs.agreement === undefined ? '' : prs.agreement ? '同意' : '未同意' },
        checkVisible('resident_agreement') && { title: '住基系外部提供同意', value: prs.residentAgreement === undefined ? '' : prs.residentAgreement ? '同意' : '未同意' },
        checkVisible('municipality_agreement') && { title: '個別避難計画外部提供同意(自治体職員のみ)', value: prs.municipalityAgreement === undefined ? '' : prs.municipalityAgreement ? '同意' : '未同意' },
        checkVisible('status') && { 
          title: '進捗状況', 
          value: progressStatusCodes.find((x) => x.code === prs.status)?.Label,
        },
      ].filter(Boolean) as AppListItem[];

      const considerationItems = [
        checkVisible('prskubun') && { title: '区分', value: prs.prsKubun },
        checkVisible('family_structure') && {
          title: (
            <>
              世帯状況
              <br />
              /家族構成
            </>
          ),
          value: prs.familyStructure,
        },
        checkVisible('solitude_cohabitation') && {
          title: '独居/同居',
          value: solitudeCohabitationCodes.find((x) => x.code === prs.solitudeCohabitation)?.Label,
        },
        {
          title: '所属地域',
          value:
            prs.soshiki.length > 0
              ? prs.soshiki.map((x) => x.soshikiName).join(appConst.LABEL_HALF_COMMA + appConst.LABEL_HALF_SPACE)
              : '未設定',
        },
        checkVisible('primary_doctor') && { title: '医療機関', value: prs.primaryDoctor },
        checkVisible('physical_disability_certificate') && {
          title: '身体障害手帳',
          value: gradeCodes.find((x) => x.code === prs.physicalDisabilityCertificate)?.Label,
        },
        checkVisible('rehabilitation_notebook') && {
          title: '療育手帳',
          value: rehabilitationNotebookCodes.find((x) => x.code === prs.rehabilitationNotebook)?.Label,
        },
        checkVisible('mental_disability_health_and_welfare_handbook') && {
          title: '精神障害者保健福祉手帳',
          value: mentalDisabilityHealthAndWelfareHandbookCodes.find(
            (x) => x.code === prs.mentalDisabilityHealthAndWelfareHandbook
          )?.Label,
        },
        checkVisible('nursing_care_level') && {
          title: '要介護度', value: nursingCareLevelCodes.find((x) => x.code === prs.nursingCareLevel)?.Label 
        },
        checkVisible('intractable_disease') && {
          title: '難病指定の有無',
          value: presenceAbsenceCodes.find((x) => x.code === prs.intractableDisease)?.Label,
        },
        checkVisible('travel_medicine') && { title: '携行薬品等', value: prs.travelMedicine },
      ].filter(Boolean) as AppListItem[];

      const emergencyContactItems = [
        checkVisible('emergency_contact_name') && { title: '氏名', value: prs.emergencyContactName },
        checkVisible('emergency_contact_name_kana') && { title: '氏名カナ', value: prs.emergencyContactNameKana },
        checkVisible('emergency_contact_relationship') && { title: '続柄', value: prs.emergencyContactRelationship },
        checkVisible('emergency_contact_address') && { title: '住所', value: prs.emergencyContactAddress },
        checkVisible('emergency_contact_phone_number') && {
          title: '電話番号',
          value: prs.emergencyContactPhoneNumber && (
            <PhoneNumberLink 
              phoneNumber={prs.emergencyContactPhoneNumber}
              message="緊急連絡先に電話をかけますか？"
              onClick={clickPhoneNumberCallback}            
            />
          ),
        },
        checkVisible('emergency_contact_phone_number2') && {
          title: '電話番号２',
          value: prs.emergencyContactPhoneNumber2 && (
            <PhoneNumberLink 
              phoneNumber={prs.emergencyContactPhoneNumber2}
              message="緊急連絡先に電話をかけますか？"
              onClick={clickPhoneNumberCallback}            
            />
          ),
        },
        checkVisible('emergency_contact_solitude_cohabitation') && {
          title: '同居/別居',
          value: cohabitationSeparationCodes.find((x) => x.code === prs.emergencyContactSolitudeCohabitation)?.Label,
        },
      ].filter(Boolean) as AppListItem[];

      const emergencyContact2Items = [
        checkVisible('emergency_contact2_name') && { title: '氏名', value: prs.emergencyContact2Name },
        checkVisible('emergency_contact2_name_kana') && { title: '氏名カナ', value: prs.emergencyContact2NameKana },
        checkVisible('emergency_contact2_relationship') && { title: '続柄', value: prs.emergencyContact2Relationship },
        checkVisible('emergency_contact2_address') && { title: '住所', value: prs.emergencyContact2Address },
        checkVisible('emergency_contact2_phone_number') && {
          title: '電話番号',
          value: prs.emergencyContact2PhoneNumber && (
            <PhoneNumberLink 
              phoneNumber={prs.emergencyContact2PhoneNumber}
              message="緊急連絡先２に電話をかけますか？"
              onClick={clickPhoneNumberCallback}            
            />
          ),
        },
        checkVisible('emergency_contact2_phone_number2') && {
          title: '電話番号２',
          value: prs.emergencyContact2PhoneNumber2 && (
            <PhoneNumberLink 
              phoneNumber={prs.emergencyContact2PhoneNumber2}
              message="緊急連絡先２に電話をかけますか？"
              onClick={clickPhoneNumberCallback}            
            />
          ),
        },
        checkVisible('emergency_contact2_solitude_cohabitation') && {
          title: '同居/別居',
          value: cohabitationSeparationCodes.find((x) => x.code === prs.emergencyContact2SolitudeCohabitation)?.Label,
        },
      ].filter(Boolean) as AppListItem[];

      const emergencyContact3Items = [
        checkVisible('emergency_contact3_name') && { title: '氏名', value: prs.emergencyContact3Name },
        checkVisible('emergency_contact3_name_kana') && { title: '氏名カナ', value: prs.emergencyContact3NameKana },
        checkVisible('emergency_contact3_relationship') && { title: '続柄', value: prs.emergencyContact3Relationship },
        checkVisible('emergency_contact3_address') && { title: '住所', value: prs.emergencyContact3Address },
        checkVisible('emergency_contact3_phone_number') && {
          title: '電話番号',
          value: prs.emergencyContact3PhoneNumber && (
            <PhoneNumberLink 
              phoneNumber={prs.emergencyContact3PhoneNumber}
              message="緊急連絡先３に電話をかけますか？"
              onClick={clickPhoneNumberCallback}            
            />
          ),
        },
        checkVisible('emergency_contact3_phone_number2') && {
          title: '電話番号２',
          value: prs.emergencyContact3PhoneNumber2 && (
            <PhoneNumberLink 
              phoneNumber={prs.emergencyContact3PhoneNumber2}
              message="緊急連絡先３に電話をかけますか？"
              onClick={clickPhoneNumberCallback}            
            />
          ),
        },
        checkVisible('emergency_contact3_solitude_cohabitation') && {
          title: '同居/別居',
          value: cohabitationSeparationCodes.find((x) => x.code === prs.emergencyContact3SolitudeCohabitation)?.Label,
        },
      ].filter(Boolean) as AppListItem[];

      const remarksItems = [checkVisible('supporter_remarks') &&{ value: prs.supporterRemarks }].filter(Boolean) as AppListItem[];

      const additionalItems: AppListItem[] = additionalItemSettings
        ? additionalItemSettings.map((setting) => {
            const matched = prs.additionalItems.find(
              (item) => item.additionalItemIndex === setting.additionalItemIndex
            );
            return {
              title: setting.additionalItemName,
              value: matched?.additionalItemValue ?? '',
            };
          })
        : [];

      setSectionDataList([
        {
          header: '基本情報',
          items: basicItems,
        },
        ...(considerationItems.length > 0
          ? [{ header: '配慮情報', items: considerationItems }]
          : []),
        ...(emergencyContactItems.length > 0
          ? [{ header: '緊急連絡先', items: emergencyContactItems }]
          : []),
        ...(emergencyContact2Items.length > 0
          ? [{ header: '緊急連絡先２', items: emergencyContact2Items }]
          : []),
        ...(emergencyContact3Items.length > 0
          ? [{ header: '緊急連絡先３', items: emergencyContact3Items }]
          : []),
        ...(remarksItems.length > 0
          ? [{ header: '備考', items: remarksItems }]
          : []),
        ...(additionalItems.length > 0
          ? [{ header: '追加情報', items: additionalItems }]
          : []),
      ]);
    } else {
      setSectionDataList(suspendDataList);
    }
  }, [
    prs,
    additionalItemSettings,
    suspendDataList,
    checkVisible,
    clickPhoneNumberCallback,
  ]);

  const clearSectionDataList = useCallback(() => {
    setSectionDataList(suspendDataList);
  }, [suspendDataList]);

  return { sectionDataList, clearSectionDataList };
};
